// AI-CONTEXT: See docs/features/msi-effect-monetisation.md
import { useEffect, useState } from 'react'
import { monetisedStandaardVersions, standaardDocumentUrl } from '../helpers/meetstandaard/standaardVersions'
import buildScoresFromStandaardDocument, { mergeScoreMaps } from '../helpers/meetstandaard/buildScoresFromStandaardDocument'

// Live source of the MSI monetisation amounts: the meetstandaard database
// carries a scores[] array per effect ({ score 1..5, situation, monetaryValue,
// onderbouwing }). Same fail-visibly + week-cache pattern as
// useParticipationParameters, with its own cache key — the two monetisation
// streams stay fully separate.
//
// TWO catalogues feed one score map, because two API pipelines are live at once:
//   legacy    /database                          → arbeidsparticipatie, gelijke-kansen
//   versioned /meetstandaard/{sector}/{version}  → energiearmoede (and the rest later)
// energiearmoede will NEVER appear in /database (confirmed by the api team), so
// reading only the legacy catalogue left every adopted energiearmoede effect
// without scores — flagged `noScores` and shown as €0 on the tenant card.
const DATABASE_URL = 'https://us-central1-meetstandaard-api.cloudfunctions.net/database'

// v2: the cached map gained the versioned sectors. Reusing the old key would
// keep serving the legacy-only map — i.e. energiearmoede at €0 — for up to a
// week after this ships, on every browser that already has a warm cache.
// v3: versioned keys carry their version (`energiearmoede:slug@0.9`). A warm v2
// map has bare keys, so every energiearmoede effect would read as noScores.
const CACHE_KEY = 'msiMonetaryScoresCacheV3'
const WEEK_MS = 7 * 24 * 60 * 60 * 1000

/**
 * @typedef {object} MsiScoreLevel
 * @property {number} score - Discrete score level (1..5)
 * @property {string} [situation] - Description of this level
 * @property {number} monetaryValue - Yearly societal value in euros (can be negative)
 * @property {string} [onderbouwing] - Justification text with source references
 */

/**
 * Flattens the meetstandaard /database payload to a map of effect id →
 * scores[], keeping only effects that actually carry monetary scores.
 * Match on `MSIId`, never on effect name — the API contains duplicate names
 * and trailing-space names. When an id appears twice, the first entry wins.
 *
 * @param {Array<{ effects?: Array<{ id?: string, scores?: Array<MsiScoreLevel> }> }>} database - the raw API payload
 * @returns {Object<string, Array<MsiScoreLevel>>}
 */
export const buildScoresByMsiId = (database) => {
    const map = {}
    if (!Array.isArray(database)) return map
    database.forEach((category) => {
        const effects = Array.isArray(category?.effects) ? category.effects : []
        effects.forEach((effect) => {
            if (!effect?.id || map[effect.id]) return
            if (!Array.isArray(effect.scores) || effect.scores.length === 0) return
            map[effect.id] = effect.scores
        })
    })
    return map
}

const loadCached = () => {
    try {
        const raw = localStorage.getItem(CACHE_KEY)
        if (!raw) return null
        return JSON.parse(raw)
    } catch (e) {
        console.warn('Failed to parse MSI monetary scores cache', e)
        return null
    }
}

/**
 * Voor welke versies de bedragen gelden, bijv. 'energiearmoede@0.12,wijkbedrijven@0.3'.
 *
 * Gaat mee in de cache en moet bij het lezen gelijk zijn aan de huidige pins.
 * Een nieuwe CACHE_KEY bij elke pinwijziging was de afspraak, en die werd bij
 * de stap naar 0.11 vergeten: een cache van vóór die stap had geen @0.11 en elk
 * energiearmoede-effect las een week lang als 'Geen bedragen in de meetstandaard'.
 * Zo ververst hij vanzelf. AI-CONTEXT: See docs/pitfalls.md#dev-mirrored-constant-guarded-only-by-a-comment
 *
 * @returns {string}
 */
const versionsSignature = () => monetisedStandaardVersions()
    .map(([sector, version]) => `${sector}@${version}`)
    .join(',')

const saveCached = (data) => {
    try {
        localStorage.setItem(CACHE_KEY, JSON.stringify({ data, fetchedAt: Date.now(), versions: versionsSignature() }))
    } catch (e) {
        console.warn('Failed to save MSI monetary scores cache', e)
    }
}

// In-flight dedup: on a cold cache many instances mount at once (both KPI
// cards + every ScoreBlock) — share one network fetch instead of firing one
// per instance. Cleared afterwards so a failure can be retried; successful
// results are served from localStorage anyway.
let pendingFetch = null

const fetchJson = async (url) => {
    const res = await fetch(url)
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    return res.json()
}

// Both catalogues or neither. A versioned document that fails must NOT fall
// back to the legacy-only map: that is exactly the bug this merge fixes, and a
// €0 effect is indistinguishable from an effect nobody answered yet.
const fetchScoresShared = () => {
    if (!pendingFetch) {
        pendingFetch = (async () => {
            // Every version still in use, not only the pin: an effect adopted
            // under 0.9 keeps 0.9's amounts (methodiek §5.3).
            const versions = monetisedStandaardVersions()
            const [legacy, ...standaarden] = await Promise.all([
                fetchJson(DATABASE_URL),
                ...versions.map(([sector, version]) => fetchJson(standaardDocumentUrl(sector, version))),
            ])
            const map = mergeScoreMaps(
                buildScoresByMsiId(legacy),
                standaarden.map(buildScoresFromStandaardDocument),
            )
            saveCached(map)
            return map
        })().finally(() => { pendingFetch = null })
    }
    return pendingFetch
}

/**
 * Fetches the meetstandaard database and exposes the monetary scores per MSI
 * effect id, caching the mapped result in localStorage for a week. On failure
 * it surfaces the error and returns no map (fail visibly — callers must skip
 * computation when `scoresByMsiId` is null, never compute against partial data).
 *
 * @returns {{ scoresByMsiId: Object<string, Array<MsiScoreLevel>> | null, loading: boolean, error: Error | null }}
 */
const useMsiMonetaryScores = () => {
    const [scoresByMsiId, setScoresByMsiId] = useState(/** @type {Object<string, Array<MsiScoreLevel>> | null} */ (null))
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(/** @type {Error | null} */ (null))

    useEffect(() => {
        const cached = loadCached()
        if (cached && Date.now() - cached.fetchedAt < WEEK_MS && cached.data && cached.versions === versionsSignature()) {
            setScoresByMsiId(cached.data)
            setLoading(false)
            return
        }

        let cancelled = false

        const fetchScores = async () => {
            try {
                const map = await fetchScoresShared()
                if (cancelled) return

                setScoresByMsiId(map)
                setError(null)
                setLoading(false)
            } catch (err) {
                console.error('Error fetching MSI monetary scores', err)
                if (cancelled) return

                setError(err)
                setScoresByMsiId(null)
                setLoading(false)
            }
        }

        fetchScores()

        return () => {
            cancelled = true
        }
    }, [])

    return { scoresByMsiId, loading, error }
}

export default useMsiMonetaryScores
